/**
 * Per-Project (Folder) task rollup for the Space Overview — the #2 "Project
 * report". One row per Folder ("Project") in the Space plus an "Unfiled"
 * bucket for lists with no folder, showing total / done / open / overdue and a
 * completion bar. Filterable by nature (small, per-Space) and CSV-exportable
 * via the shared client-side `runExport`. Data comes from the loose
 * `overview.projects` payload (space-overview.service).
 */

import { FolderKanban, Download } from 'lucide-react'
import { Button } from '../ui/Button'
import { runExport } from '../table/tableExport'
import { todayKey } from '../../lib/format'

const PROJECT_EXPORT_COLS = [
  { key: 'name', header: 'Project' },
  { key: 'total', header: 'Total' },
  { key: 'done', header: 'Done' },
  { key: 'open', header: 'Open' },
  { key: 'overdue', header: 'Overdue' },
  { key: 'completionPct', header: 'Completion %' },
]

export function SpaceOverviewProjects({ overview }) {
  const projects = overview?.projects ?? []
  if (projects.length === 0) return null
  const spaceName = overview?.space?.name ?? 'space'

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <div className="mb-3 flex items-center gap-2">
        <FolderKanban size={16} className="text-[color:var(--color-ink-muted)]" />
        <p className="font-display text-lg font-medium tracking-tight text-[color:var(--color-ink-strong)]">
          Projects
        </p>
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          ({projects.length})
        </span>
        <div className="ml-auto">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              runExport('CSV', projects, PROJECT_EXPORT_COLS, {
                baseName: `${spaceName}-projects`,
                title: 'Projects',
                isoDate: todayKey(),
              })
            }
            title={`Export ${projects.length} project${projects.length === 1 ? '' : 's'} to CSV`}
          >
            <Download size={14} /> Export
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)]">
              <th className="px-2 py-2 font-medium">Project</th>
              <th className="px-2 py-2 font-medium text-right tabular-nums">Total</th>
              <th className="px-2 py-2 font-medium text-right tabular-nums">Done</th>
              <th className="px-2 py-2 font-medium text-right tabular-nums">Open</th>
              <th className="px-2 py-2 font-medium text-right tabular-nums">Overdue</th>
              <th className="px-2 py-2 font-medium w-40">Completion</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr
                key={p.folderId ?? 'unfiled'}
                className="border-b border-[color:var(--color-rule)] last:border-b-0"
              >
                <td className="px-2 py-2 text-[color:var(--color-ink-strong)]">
                  <span
                    className={
                      p.folderId == null
                        ? 'italic text-[color:var(--color-ink-muted)]'
                        : ''
                    }
                  >
                    {p.name}
                  </span>
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-[color:var(--color-ink)]">
                  {p.total}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-[color:var(--color-ink)]">
                  {p.done}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-[color:var(--color-ink)]">
                  {p.open}
                </td>
                <td
                  className={
                    'px-2 py-2 text-right tabular-nums ' +
                    (p.overdue > 0
                      ? 'text-[color:var(--color-priority-urgent)] font-medium'
                      : 'text-[color:var(--color-ink)]')
                  }
                >
                  {p.overdue}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-[color:var(--color-canvas)] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[color:var(--color-success)]"
                        style={{ width: `${p.completionPct}%` }}
                      />
                    </div>
                    <span className="w-9 text-right text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                      {p.completionPct}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
